import React, { useState, useEffect } from 'react';
import {
  Wallet, FileText, Users, ShieldCheck, ArrowDownUp,
  Truck, BarChart3, BookOpen, CreditCard, Coins,
  FolderOpen, Package, ChevronRight, ArrowLeft,
  Search, TrendingUp, TrendingDown, AlertCircle,
  CircleDollarSign, ClipboardCheck 
} from 'lucide-react';
import { useLoader } from '../contexts/LoaderContext';
import { ParametresComptaProvider, useParamsCompta } from '../contexts/ParametresComptaContext';
import api from '../services/api';

import PaieModule from './comptabilite/PaieModule';
import AvancesModule from './comptabilite/AvancesModule';
import RegistreModule from './comptabilite/RegistreModule';
import TresorerieModule from './comptabilite/TresorerieModule';
import PartenairesModule from './comptabilite/PartenairesModule';
import FacturationModule from './comptabilite/FacturationModule';
import ValidationModule from './comptabilite/ValidationModule';
import ReportingModule from './comptabilite/ReportingModule';
import CommandesModule from './comptabilite/CommandesModule';
import LivraisonsModule from './comptabilite/LivraisonsModule';
import PlanComptableModule from './comptabilite/PlanComptableModule';
import StocksModule from './comptabilite/StocksModule';
import ApprobationsModule from './comptabilite/ApprobationsModule';
import PrimesModule from './comptabilite/PrimesModule';

const MODULE_COMPONENTS = {
  tresorerie: TresorerieModule,
  facturation: FacturationModule,
  partenaires: PartenairesModule,
  commandes: CommandesModule,
  livraisons: LivraisonsModule,
  stocks: StocksModule,
  validation: ValidationModule,
  paie: PaieModule,
  avances: AvancesModule,
  reporting: ReportingModule,
  plan_comptable: PlanComptableModule,
  registre: RegistreModule,
  approbation : ApprobationsModule,
  primes: PrimesModule
};

const MODULES = [
  { id: 'tresorerie', title: 'Trésorerie', subtitle: 'Caisses, mouvements & transferts', icon: Wallet, color: '#448696', gradient: 'linear-gradient(135deg, #448696 0%, #448696 100%)' },
  { id: 'facturation', title: 'Facturation & Devis', subtitle: 'Factures, devis & encaissements', icon: FileText, color: '#6366f1', gradient: 'linear-gradient(135deg, #6366f1 0%, #818cf8 100%)' },
  { id: 'partenaires', title: 'Partenaires', subtitle: 'Clients, fournisseurs & contacts', icon: Users, color: '#0ea5e9', gradient: 'linear-gradient(135deg, #0ea5e9 0%, #38bdf8 100%)' },
  { id: 'commandes', title: 'Bons de Commande', subtitle: 'Commandes achat & vente', icon: ArrowDownUp, color: '#f97316', gradient: 'linear-gradient(135deg, #f97316 0%, #fb923c 100%)' },
  { id: 'stocks', title: 'Gestion de Stocks', subtitle: 'Articles, entrepôts & inventaire', icon: Package, color: '#8b5cf6', gradient: 'linear-gradient(135deg, #8b5cf6 0%, #a78bfa 100%)' },
  { id: 'livraisons', title: 'Livraisons', subtitle: 'Réceptions & expéditions', icon: Truck, color: '#ec4899', gradient: 'linear-gradient(135deg, #ec4899 0%, #f472b6 100%)' },
  { id: 'validation', title: 'Validation Dépenses', subtitle: 'Approbation & exécution', icon: ShieldCheck, color: '#10b981', gradient: 'linear-gradient(135deg, #10b981 0%, #34d399 100%)' },
  { id: 'paie', title: 'Gestion de la Paie', subtitle: 'Salaires & pointages', icon: CreditCard, color: '#448696', gradient: 'linear-gradient(135deg, #448696 0%, #a8d0d9 100%)' },
  { id: 'avances', title: 'Avances sur Salaire', subtitle: 'Demandes & remboursements', icon: Coins, color: '#d4a017', gradient: 'linear-gradient(135deg, #d4a017 0%, #fed774 100%)' },
  { id: 'primes', title: 'Primes', subtitle: 'Attribution & gestion des primes', icon: TrendingUp, color: '#16a34a', gradient: 'linear-gradient(135deg, #16a34a 0%, #4ade80 100%)' },
  { id: 'reporting', title: 'Reporting Financier', subtitle: 'Dashboard & analyses', icon: BarChart3, color: '#14b8a6', gradient: 'linear-gradient(135deg, #14b8a6 0%, #2dd4bf 100%)' },
  { id: 'plan_comptable', title: 'Plan Comptable', subtitle: 'Comptes OHADA & classification', icon: BookOpen, color: '#78716c', gradient: 'linear-gradient(135deg, #78716c 0%, #a8a29e 100%)' },
  { id: 'registre', title: 'Registre Comptable', subtitle: 'Historique des paiements', icon: FolderOpen, color: '#64748b', gradient: 'linear-gradient(135deg, #64748b 0%, #94a3b8 100%)' },
  { id: 'approbation', title: 'Approbations', subtitle: 'validation des oépations', icon: ClipboardCheck , color: '#ec5b48', gradient: 'linear-gradient(135deg, #ec5b49 0%, #ec5b48 100%)' },
];

const ComptabiliteHub = () => {
  const [activeModule, setActiveModule] = useState(null);
  const [hoveredCard, setHoveredCard] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [kpi, setKpi] = useState({ solde: 0, revenus: 0, depenses: 0, impayes: 0 });

  const { showLoader, hideLoader } = useLoader();
  const { formatMontant, formatMontantCourt, symbole } = useParamsCompta();

  useEffect(() => { loadKPIs(); }, []);

  const loadKPIs = async () => {
    showLoader(); setIsLoading(true);
    try {
      let solde = 0, revenus = 0, depenses = 0, impayes = 0;

      // Solde global depuis les caisses
      try {
        const r = await api.tresorerie.getCaisses();
        if (r.success) {
          if (r.solde_global !== undefined) {
            solde = parseFloat(r.solde_global || 0);
          } else if (Array.isArray(r.data)) {
            solde = r.data.reduce((s, c) => s + parseFloat(c.solde_actuel || 0), 0);
          }
        }
      } catch (e) { console.error('KPI solde:', e); }

      // Revenus & Dépenses — essayer tresorerie dashboard puis reporting
      try {
        const r = await api.tresorerie.getDashboard();
        if (r?.success && r.data) {
          revenus = parseFloat(r.data.total_credits_mois || r.data.total_credits || r.data.revenus_mois || 0);
          depenses = parseFloat(r.data.total_debits_mois || r.data.total_debits || r.data.depenses_mois || 0);
        }
        // Fallback reporting si rien
        if (revenus === 0 && depenses === 0) {
          const now = new Date();
          const r2 = await api.reporting.getGlobal({ mois: now.getMonth() + 1, annee: now.getFullYear() });
          if (r2?.success && r2.data) {
            revenus = parseFloat(r2.data.revenus_mois || r2.data.total_credits || 0);
            depenses = parseFloat(r2.data.depenses_mois || r2.data.total_debits || 0);
          }
        }
      } catch (e) { console.error('KPI revenus/depenses:', e); }

      // Factures impayées
      try {
        const r = await api.facturation.getAll({});
        if (r?.success && Array.isArray(r.data)) {
          impayes = r.data.filter(f => f.type_document === 'facture' && ['envoyee', 'partielle', 'en_retard'].includes(f.statut)).length;
        }
      } catch (e) { console.error('KPI factures:', e); }

      setKpi({ solde, revenus, depenses, impayes });
    } catch (e) { console.error(e); }
    finally { setIsLoading(false); hideLoader(); }
  };

  const fmt = (v, isCurrency) => isCurrency ? formatMontantCourt(v) : v;

  const filtered = MODULES.filter(m =>
    m.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    m.subtitle.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // ============================================================================
  // MODULE ACTIF
  // ============================================================================
  if (activeModule) {
    const cur = MODULES.find(m => m.id === activeModule);
    const Icon = cur?.icon || CircleDollarSign;
    const Comp = MODULE_COMPONENTS[activeModule];

    return (
      <div className="min-h-screen" style={{ backgroundColor: '#fdfcf5' }}>
        {/* Barre flottante */}
        <div className="sticky top-0 z-40 px-5 pt-3 pb-1" style={{ backgroundColor: '#fdfcf5' }}>
          <div className="bg-white rounded-2xl px-5" style={{ boxShadow: '0 2px 16px rgba(0,0,0,0.06)', border: '1px solid #ebebeb' }}>
            <div className="flex items-center justify-between h-14">
              <div className="flex items-center gap-1.5">
                <button onClick={() => setActiveModule(null)}
                  className="flex items-center gap-2 px-3 py-2 rounded-xl text-sm font-bold transition-all hover:bg-gray-50"
                  style={{ color: '#448696' }}>
                  <ArrowLeft size={18} strokeWidth={2.5} /> Comptabilité
                </button>
                <ChevronRight size={16} className="text-gray-300" />
                <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl" style={{ backgroundColor: cur.color + '12' }}>
                  <div className="w-6 h-6 rounded-lg flex items-center justify-center" style={{ background: cur.gradient }}>
                    <Icon size={13} className="text-white" />
                  </div>
                  <span className="text-sm font-bold" style={{ color: cur.color }}>{cur.title}</span>
                </div>
              </div>

              <div className="flex items-center gap-1 rounded-xl px-1.5 py-1" style={{ backgroundColor: '#f6f6f4', border: '1px solid #ebebeb' }}>
                {MODULES.map(m => {
                  const N = m.icon; const a = m.id === activeModule;
                  return (
                    <button key={m.id} onClick={() => setActiveModule(m.id)} title={m.title}
                      className="p-1.5 rounded-lg transition-all"
                      style={{ backgroundColor: a ? m.color : 'transparent', color: a ? '#fff' : '#94a3b8', boxShadow: a ? `0 2px 8px ${m.color}35` : 'none' }}
                      onMouseEnter={(e) => { if (!a) { e.currentTarget.style.backgroundColor = '#e8e8e8'; e.currentTarget.style.color = '#374151'; } }}
                      onMouseLeave={(e) => { if (!a) { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = '#94a3b8'; } }}>
                      <N size={14} />
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div>{Comp ? <Comp /> : null}</div>
      </div>
    );
  }

  // ============================================================================
  // HUB
  // ============================================================================
  const KPI_CARDS = [
    { label: 'Solde global', value: kpi.solde, currency: true, icon: Wallet, color: '#448696', bg: '#f0f9fb' },
    { label: 'Revenus du mois', value: kpi.revenus, currency: true, icon: TrendingUp, color: '#10b981', bg: '#ecfdf5' },
    { label: 'Dépenses du mois', value: kpi.depenses, currency: true, icon: TrendingDown, color: '#ef4444', bg: '#fef2f2' },
    { label: 'Factures impayées', value: kpi.impayes, currency: false, icon: AlertCircle, color: '#f59e0b', bg: '#fffbeb' },
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#fdfcf5' }}>
     <div className="px-6 pt-6 pb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-1" style={{ color: '#448696' }}>Comptabilité & Finance</h1>
            <p className="text-gray-500 text-sm font-medium">Gestion complète des flux financiers</p>
          </div>
          <div className="text-right text-xs text-gray-400 font-medium">
            <div>Exercice {new Date().getFullYear()}</div>
          </div>
        </div>
      </div>

      <div className="px-6 pb-5">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {KPI_CARDS.map((k, i) => {
            const K = k.icon;
            return (
              <div key={i} className="bg-white rounded-xl p-5 hover:shadow-md transition-all"
                style={{ borderLeft: `3px solid ${k.color}`, boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: k.bg }}>
                    <K size={20} style={{ color: k.color }} />
                  </div>
                  <span className="text-xs font-semibold text-gray-500">{k.label}</span>
                </div>
                {isLoading
                  ? <div className="h-7 w-28 bg-gray-100 rounded-lg animate-pulse" />
                  : <div className="text-2xl font-bold" style={{ color: '#1a1a2e' }}>{k.currency ? formatMontantCourt(k.value) : k.value}</div>
                }
              </div>
            );
          })}
        </div>
      </div>

      <div className="px-6 pb-4">
        <div className="flex items-center justify-between">
          <div className="relative" style={{ width: '300px' }}>
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input type="text" placeholder="Rechercher un module..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium focus:outline-none transition-all"
              onFocus={(e) => { e.target.style.borderColor = '#448696'; e.target.style.boxShadow = '0 0 0 3px rgba(136,185,197,0.12)'; }}
              onBlur={(e) => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }} />
          </div>
          <span className="text-xs font-semibold text-gray-400">{MODULES.length} modules</span>
        </div>
      </div>

      <div className="px-6 pb-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {filtered.map((m) => {
            const Icon = m.icon;
            const h = hoveredCard === m.id;
            return (
              <div key={m.id} onClick={() => setActiveModule(m.id)}
                onMouseEnter={() => setHoveredCard(m.id)} onMouseLeave={() => setHoveredCard(null)}
                className="bg-white rounded-2xl overflow-hidden transition-all duration-300 cursor-pointer"
                style={{
                  border: `1.5px solid ${h ? m.color + '40' : '#f0f0ee'}`,
                  boxShadow: h ? `0 10px 32px -8px ${m.color}20, 0 4px 12px rgba(0,0,0,0.06)` : '0 1px 3px rgba(0,0,0,0.03)',
                  transform: h ? 'translateY(-3px)' : 'translateY(0)',
                }}>
                <div className="h-1 transition-all" style={{ background: m.gradient, opacity: h ? 1 : 0.4 }} />
                <div className="p-5">
                  <div className="mb-4">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center transition-all duration-300"
                      style={{ background: h ? m.gradient : m.color + '0d', boxShadow: h ? `0 4px 14px ${m.color}30` : 'none' }}>
                      <Icon size={22} style={{ color: h ? '#fff' : m.color }} />
                    </div>
                  </div>
                  <h3 className="text-[15px] font-bold mb-0.5 transition-colors" style={{ color: h ? m.color : '#1a1a2e' }}>{m.title}</h3>
                  <p className="text-xs font-medium text-gray-400 mb-4">{m.subtitle}</p>
                  <div className="flex items-center justify-between pt-3 border-t" style={{ borderColor: h ? m.color + '15' : '#f5f5f3' }}>
                    <span className="text-[11px] font-bold" style={{ color: h ? m.color : '#d1d5db' }}>Accéder</span>
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center transition-all duration-300"
                      style={{ backgroundColor: h ? m.color : 'transparent', transform: h ? 'translateX(0)' : 'translateX(-4px)', opacity: h ? 1 : 0.3 }}>
                      <ChevronRight size={14} style={{ color: h ? '#fff' : '#d1d5db' }} />
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Wrapper avec le Provider des paramètres comptabilité
const ComptabiliteHubWithProvider = () => (
  <ParametresComptaProvider>
    <ComptabiliteHub />
  </ParametresComptaProvider>
);

export default ComptabiliteHubWithProvider;